import React, { useRef, useState } from 'react';
import styles from './EditName.module.scss';
import classNames from 'classnames';
import {
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

export interface EditNameProps {
  value: string;
  placeholder?: string;
  onChange: (next: string) => void; // for controlled display value
  onCommit: (next: string) => void; // when save is confirmed
  className?: string;
  titleClassName?: string;
  inputClassName?: string;
}

const EditName: React.FC<EditNameProps> = ({
  value,
  placeholder,
  onChange,
  onCommit,
  className,
  titleClassName,
  inputClassName,
}) => {
  const [editing, setEditing] = useState(!Boolean(value?.trim()));
  const [error, setError] = useState(false);
  const prevRef = useRef<string>(value || '');
  const inputRef = useRef<HTMLInputElement>(null);

  const save = () => {
    if (!value) {
      setError(true);
      inputRef.current?.focus({ preventScroll: true });
      return;
    }
    prevRef.current = value;
    onCommit(value);
    setEditing(false);
  };

  const cancel = () => {
    onChange(prevRef.current);
    setEditing(false);
    setError(false);
  };

  // When the external value is empty, force edit mode and focus input so users clearly see it's required
  React.useEffect(() => {
    if (!value?.trim()) {
      setEditing(true);
      requestAnimationFrame(() => inputRef.current?.focus({ preventScroll: true }));
    }
  }, [value]);

  return (
    <div className={classNames(styles.editName, className)}>
      {!editing ? (
        <div className={styles.displayRow}>
          <Typography
            className={classNames(styles.titleText, titleClassName)}
            title={value || placeholder}
            onClick={() => {
              setEditing(true);
              requestAnimationFrame(() => {
                inputRef.current?.focus({ preventScroll: true });
                inputRef.current?.select();
              });
            }}
          >
            {value || placeholder}
          </Typography>
          <Tooltip title="Edit name">
            <IconButton
              className={styles.editIcon}
              size="small"
              onClick={() => {
                setEditing(true);
                requestAnimationFrame(() => {
                  inputRef.current?.focus({ preventScroll: true });
                  inputRef.current?.select();
                });
              }}
              aria-label="Edit name"
            >
              <DriveFileRenameOutlineOutlinedIcon
                style={{ color: 'var(--color-text-3)' }}
              />
            </IconButton>
          </Tooltip>
        </div>
      ) : (
        <div className={styles.inputRow}>
          <TextField
            fullWidth
            inputRef={inputRef}
            color="primary"
            name="editName"
            placeholder={placeholder}
            value={value}
            className={classNames(styles.nameTextField, inputClassName, {
              // map error styling to MUI error prop for underline/border changes if needed
            })}
            error={error}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              if (e.target.value.length > 0) {
                setError(false);
              }
              onChange(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                save();
              } else if (e.key === 'Escape') {
                cancel();
              }
            }}
            onBlur={() => {
              if (!editing) return;
              save();
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={save}
                    disabled={!Boolean(value?.trim())}
                    aria-label="Save"
                  >
                    <CheckRoundedIcon
                      style={{ color: 'var(--color-text-3)' }}
                    />
                  </IconButton>
                  <IconButton
                    size="small"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={cancel}
                    disabled={!Boolean(value?.trim())}
                    aria-label="Cancel"
                  >
                    <CloseRoundedIcon color="primary" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            variant="outlined"
            size="small"
            sx={{
              '& .MuiInputBase-input': {
                fontSize: '18px',
                fontWeight: 'bold',
                color: 'var(--color-accent)',
                padding: '6px 8px',
              },
              '& .MuiInputBase-input::placeholder': {
                color: 'var(--color-text-1)',
                fontSize: '14px',
              },
            }}
          />
        </div>
      )}
    </div>
  );
};

export default EditName;
